import { alertsApi, type AlertFrame } from '#entities';
import { resourceUpdated } from '#shared';

import type { AppDispatch, RootState } from '#shared';

/**
 * Applies one alert transition to the snapshot cache.
 *
 * `raised` and `updated` are the same operation — an upsert keyed by `id`, the
 * lifecycle identity — and `cleared` is a removal by that same key. Those are
 * the client obligations BE ARCH-010 records, and they are why a duplicate frame
 * is harmless: live and snapshot copies of one alert differ in `ts`, so dedup is
 * by `id` plus `transition` and never by frame equality (377:E025).
 *
 * Unlike `applyDomainEventFrame` there is no sequence cursor to advance and no
 * rolling log to cap: the alert stream carries no seq, and the set is bounded by
 * what is currently true (377:E032).
 *
 * A frame arriving before the first snapshot is dropped. Unlike the domain-event
 * case there is no cursor to poison — the reason here is simply that the
 * snapshot which follows carries current truth, so nothing is lost by waiting
 * for it.
 */
export const applyAlertFrame =
  (frame: AlertFrame) =>
  (dispatch: AppDispatch, getState: () => RootState): void => {
    const cached = alertsApi.endpoints.getAlerts.select()(getState());

    if (cached.data === undefined) {
      return;
    }

    dispatch(
      alertsApi.util.updateQueryData('getAlerts', undefined, draft => {
        const at = draft.alerts.findIndex(existing => existing.id === frame.id);

        if (frame.transition === 'cleared') {
          if (at !== -1) {
            draft.alerts.splice(at, 1);
          }

          return;
        }

        if (at === -1) {
          draft.alerts.push(frame);

          return;
        }

        draft.alerts[at] = frame;
      }),
    );

    dispatch(resourceUpdated({ resource: 'alerts', atMs: Date.now() }));
  };
